import type * as zod from 'zod';

import type { CartesianPose } from '@sb/geometry/CartesianPose';
import { makeNamespacedLog } from '@sb/log';
import type {
  ArmTarget,
  PoseArmTarget,
  TCPFrame,
  TCPOffsetOption,
} from '@sb/motion-planning';
import type { ArcStep, RoutineContext, StepFailure } from '@sb/routine-runner';
import type { SpeedProfile } from '@sb/routine-runner/speed-profile';
import type WaypointStep from '@sb/routine-runner/Step/Waypoint/Step';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import type { NonEmptyArray } from '@sb/utilities/src/types';

import type { movementActivity } from './Variables';

const log = makeNamespacedLog('weldArmTargets');

/** The `doMotion` handle both weld steps hold so pause/stop can emit into it. */
export type WeldMotionHandle = ReturnType<RoutineContext['doMotion']>;

type MovementActivity = zod.infer<typeof movementActivity>;

/**
 * The relative-to-current integrator state carried across weld target
 * computations: the predecessor pose and the TCP frame it is genuinely
 * expressed in (`undefined` = frame unknown, e.g. a legacy joint-match teach
 * snapshot that stored no tcpOption — a relative read fails loud on it
 * rather than guessing). SUP-4006.
 */
export type WeldIntegratorCarry = {
  pose: CartesianPose;
  tcpFrame: TCPFrame | undefined;
};

/**
 * What a weld step brings to its own target computation.
 *
 * `getTCPOffsetOption` is a callback rather than a resolved value because the
 * original read it per use: it resolves the torch out of live equipment state
 * and throws when the torch is gone, so collapsing it to one up-front read
 * would move where that throw happens.
 *
 * `completedWeldCount` is the loop counter substeps interpolate into
 * expressions. Both weld steps carry one under that name.
 */
export type WeldTargetContext = {
  routineContext: RoutineContext;
  getTCPOffsetOption: () => TCPOffsetOption;
  completedWeldCount: number;
};

function isWaypointSubstep(
  substep: WaypointStep | ArcStep,
): substep is WaypointStep {
  return substep.getStepKind() === 'Waypoint';
}

/**
 * The flag-ON (matched relative-to-current frames) substep target call.
 *
 * Waypoint substeps get the relative-to-current frame plumbing (mirrors
 * MoveArmToV2 under constant speed): `forcedTooltip` pins both the
 * read/write frame and the commanded frame to the torch — stamped inside
 * getArmTarget BEFORE the carry tag is computed, so the tag stays truthful
 * (a joint-match substep keeps its stored teach frame). The torch is named
 * by option only; the frame carries no `gripperID`, so it resolves by name
 * against enabled equipment as it always has.
 * `capturedGlobalTCPOption` is the weld step's captured global: `_play`
 * forces the global TCP to the torch at step start.
 *
 * Arc substeps get the same torch pin without the read/write plumbing (an arc
 * has no relative-to-current read), and additionally receive the carry tag:
 * `maintainStartOrientation` copies the carried ORIENTATION onto the arc's
 * commanded targets and carry-out, so when the carry is genuinely in a
 * different frame (a joint-replay approach taught with another tool) the
 * arc re-expresses it into its commanded frame instead of commanding the
 * teach-frame quaternion under a torch-frame carry tag (SUP-4006).
 */
function getMatchedFramesSubstepTarget({
  getTCPOffsetOption,
  completedWeldCount,
  substep,
  previousTargetPose,
  previousTargetTCPFrame,
}: Pick<WeldTargetContext, 'getTCPOffsetOption' | 'completedWeldCount'> & {
  substep: WaypointStep | ArcStep;
  previousTargetPose: CartesianPose;
  previousTargetTCPFrame: TCPFrame | undefined;
}):
  | ReturnType<WaypointStep['getArmTarget']>
  | ReturnType<ArcStep['getArmTarget']> {
  if (isWaypointSubstep(substep)) {
    return substep.getArmTarget({
      parentCompletedCount: completedWeldCount,
      previousTargetPose,
      capturedGlobalTCPOption: getTCPOffsetOption(),
      previousTargetTCPFrame,
      forcedTooltip: { tcpOption: getTCPOffsetOption() },
    });
  }

  return substep.getArmTarget({
    forcedTooltip: { tcpOption: getTCPOffsetOption() },
    parentCompletedCount: completedWeldCount,
    previousTargetPose,
    previousTargetTCPFrame,
  });
}

/**
 * Get the arm targets for the given substeps.
 *
 * Optionally converts joint arm targets to pose arm targets.
 *
 * Fancy typing ensures the output array length matches the input array length, and enforces
 * the conversion to pose arm targets if requested.
 */
export async function getWeldArmTargets<
  T extends Array<WaypointStep | ArcStep>,
  P extends boolean = true,
>({
  routineContext,
  getTCPOffsetOption,
  completedWeldCount,
  substeps,
  fail,
  convertToPose = true as P,
  seed,
  onCarryOut,
}: WeldTargetContext & {
  substeps: T;
  fail: (failure: StepFailure) => void;
  convertToPose?: P;
  /**
   * Integrator start for a body computation that chains from the approach's
   * first evaluation (pose + the TCP frame it is genuinely expressed in)
   * instead of the live robot state. Consumed only on the matched-frames
   * flag (SUP-4006).
   */
  seed?: WeldIntegratorCarry;
  /**
   * Receives the final integrator carry of this computation — `_play` uses
   * it to bridge the approach's first evaluation into the body computation.
   * Called only on success.
   */
  onCarryOut?: (carry: WeldIntegratorCarry) => void;
}): Promise<
  | { [K in keyof T]: P extends true ? ArmTarget & PoseArmTarget : ArmTarget }
  | undefined
> {
  const targets: ArmTarget[] = [];

  const { kinematicState } = routineContext.getRoutineRunnerState();

  let previousTargetPose: CartesianPose =
    kinematicState.tcpOffsetOption === 'wrist'
      ? kinematicState.wristPose
      : kinematicState.tooltipPoint;

  // The TCP frame `previousTargetPose` is genuinely expressed in. Threaded
  // flag-ON only — the flag-OFF path carries no tag, exactly today's
  // behavior.
  let previousTargetTCPFrame: TCPFrame | undefined;

  // Read once per computation: it gates the seed, the substep param shape,
  // and the tag threading together, so they can never disagree mid-loop.
  const matchFramesEnabled = getFeatureFlagSync(
    'matchRelativeToCurrentFrames',
    true,
  );

  if (matchFramesEnabled) {
    if (seed) {
      // Bridged body computation: chain from the approach's first
      // evaluation rather than re-deriving from the post-move live state —
      // recomputing a relative-to-current approach from the moved pose
      // re-applies its offset, and that pose contaminated the body carry.
      previousTargetPose = seed.pose;
      previousTargetTCPFrame = seed.tcpFrame;
    } else {
      // SUP-4006: seed relative-to-current waypoints in the torch TCP,
      // derived from the flange pose (frame-independent) — `tooltipPoint`
      // is not recomputed when the active TCP changes, so the seed above
      // can still be in the pre-weld frame. Without this re-expression, the
      // move puts the flange at the offset point instead of the torch TCP:
      // a nosedive by the tool-offset difference.
      previousTargetPose = routineContext.reexpressTooltipPose(
        kinematicState.wristPose,
        { tcpOption: 'wrist' },
        { tcpOption: getTCPOffsetOption() },
      );

      // Live-state seed, freshly expressed in the torch TCP. No gripperID:
      // the torch resolves by name against enabled equipment — the same
      // resolution command consumption uses for these targets.
      previousTargetTCPFrame = { tcpOption: getTCPOffsetOption() };
    }
  }

  for (let i = 0; i < substeps.length; i += 1) {
    const substep: WaypointStep | ArcStep = substeps[i];

    if (
      substep.getStepKind() !== 'Waypoint' &&
      substep.getStepKind() !== 'Arc'
    ) {
      // This is a dev error. Will need to update once we add additional flourishes.
      throw new Error(
        `Substep is not a waypoint or arc: kind=${substep.getStepKind()}`,
      );
    }

    // Flag-OFF: today's substep call, byte-identical for Waypoint and Arc.
    // Flag-ON: substep-kind-specific plumbing — see
    // `getMatchedFramesSubstepTarget`.
    const result = await (matchFramesEnabled
      ? getMatchedFramesSubstepTarget({
          getTCPOffsetOption,
          completedWeldCount,
          substep,
          previousTargetPose,
          previousTargetTCPFrame,
        })
      : substep.getArmTarget({
          forcedTooltip: { tcpOption: getTCPOffsetOption() },
          parentCompletedCount: completedWeldCount,
          previousTargetPose,
        }));

    if ('failure' in result) {
      fail(result);

      return undefined;
    }

    const maybeTargetArray = result.target;
    const { poseForPreviousTarget } = result;

    previousTargetPose = poseForPreviousTarget;

    // Carry the tag in lock-step with `previousTargetPose`. It MUST name
    // the frame the carry pose is GENUINELY in — not always the torch: a
    // joint-match substep carries its stored teach-time pose in its stored
    // frame, and reading that carry as the torch is a dive by the
    // tool-offset gap.
    if (matchFramesEnabled) {
      previousTargetTCPFrame = result.poseForPreviousTargetTCPFrame;
    }

    const targetArray = Array.isArray(maybeTargetArray)
      ? maybeTargetArray
      : [maybeTargetArray];

    for (let target of targetArray) {
      // Only convert to pose if requested
      if (convertToPose && !('pose' in target)) {
        target = {
          ...target,
          // this is a joint arm target so lets convert it to a pose arm target
          pose: await routineContext.getTargetPose(target),
        };

        if ('jointAngles' in target) {
          // remove jointAngles from target otherwise motion planner will use them
          delete target.jointAngles;
        }
      }

      targets.push(target);
    }
  }

  onCarryOut?.({
    pose: previousTargetPose,
    tcpFrame: previousTargetTCPFrame,
  });

  return targets as {
    [K in keyof T]: P extends true ? ArmTarget & PoseArmTarget : ArmTarget;
  };
}

export async function moveToWeldPosition({
  routineContext,
  armTarget,
  speedProfile,
  stepID,
  fail,
  getApproachActivity,
  setApproachActivity,
  setMotionHandle,
}: {
  routineContext: RoutineContext;
  armTarget: ArmTarget;
  speedProfile: SpeedProfile;
  stepID: string;
  fail: (failure: StepFailure) => void;
  getApproachActivity: () => MovementActivity;
  setApproachActivity: (activity: MovementActivity) => void;
  setMotionHandle: (motion: WeldMotionHandle | undefined) => void;
}): Promise<boolean> {
  // Weld does not observe push mode: should not be pushing up against object.
  // Also, ros2 does not support push at this time (2025-02-19).
  let completed = false;

  const motion = routineContext.doMotion({
    request: {
      targets: [armTarget] as NonEmptyArray<ArmTarget>,
    },
    speedProfiles: [speedProfile],
    pushUntilCollision: false,
    pushMode: false,
    stepID,
  });

  motion.on('requestingPlan', () => {
    // Only checking previous state for first few states because we could be in a play/pause/resume flow.
    if (getApproachActivity() === 'none') {
      setApproachActivity('requestingPlan');
    }
  });

  motion.on('planning', () => {
    if (getApproachActivity() === 'requestingPlan') {
      setApproachActivity('planning');
    }
  });

  motion.on('beginMotion', () => {
    setApproachActivity('moving');
  });

  motion.on('pause', () => {
    setApproachActivity('paused');
  });

  motion.on('complete', () => {
    log.debug('moveToApproachPosition', 'Approach motion complete');

    completed = true;
  });

  motion.on('cancelled', () => {
    setApproachActivity('none');
  });

  motion.on('failure', (failure) => {
    setApproachActivity('none');
    fail(failure);
  });

  setMotionHandle(motion);

  try {
    await motion.race('failure', 'complete', 'cancelled');
  } finally {
    setApproachActivity('none');
    motion.removeAllListeners();

    setMotionHandle(undefined);
  }

  return completed;
}

/**
 * The step-scoped pin of the global motion frame to the torch.
 *
 * A weld computes its targets in the torch frame, so it writes the register
 * for the duration of the step and puts the operator's frame back on the way
 * out. The recorded write is what makes the restore safe: a register moved by
 * someone else mid-play (an operator jog under pause, a WebSocket
 * `setTCPOffsetOption`) keeps their choice.
 *
 * An object rather than a pair of functions, because the recorded write has
 * to outlive the call that made it. `routineContext` is passed per call
 * rather than held, so a step can construct this in a field initializer,
 * before the base constructor has assigned its own.
 */
export class WeldTorchFramePin {
  /**
   * The torch option this play's pin wrote to the register, recorded so the
   * end-of-play restore can tell its own write from anyone else's.
   */
  private pinnedTCPOption?: TCPOffsetOption;

  /**
   * Clear the previous play's record. A step instance is replayed (a loop
   * iteration, a play after a stop), so the restore must key on what THIS
   * play wrote.
   */
  public reset(): void {
    this.pinnedTCPOption = undefined;
  }

  public pin(routineContext: RoutineContext, tcpOffset: TCPOffsetOption): void {
    this.pinnedTCPOption = tcpOffset;
    routineContext.setTCPOffsetOption(tcpOffset);
  }

  /**
   * Put the global motion frame back where it was before this step's torch
   * pin. Restores only this step's own write; a play that failed before
   * pinning (`pinnedTCPOption` unset) restores nothing.
   */
  public restore(
    routineContext: RoutineContext,
    preStepTCPOption: TCPOffsetOption,
  ): void {
    const { tcpOffsetOption } =
      routineContext.getRoutineRunnerState().kinematicState;

    if (
      this.pinnedTCPOption != null &&
      tcpOffsetOption === this.pinnedTCPOption &&
      tcpOffsetOption !== preStepTCPOption
    ) {
      routineContext.setTCPOffsetOption(preStepTCPOption);
    }
  }
}
